import { useCallback, useLayoutEffect, useRef } from 'react';
import { resizeTextareaPreservingScroll } from '../utils/textareaAutoSize';

const useAutoResizeTextarea = ({ value, minHeight, externalRef }) => {
  const internalRef = useRef(null);
  const ref = externalRef || internalRef;

  const adjustHeight = useCallback(() => {
    resizeTextareaPreservingScroll({ textarea: ref.current, minHeight });
  }, [ref, minHeight]);

  useLayoutEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  // the same text needs more lines as the field narrows (window resize, table column
  // reflow), and overflow is hidden, so re-fit on width changes or that text is lost
  useLayoutEffect(() => {
    const textarea = ref.current;
    if (!textarea || typeof ResizeObserver === 'undefined') return undefined;

    let lastWidth = textarea.offsetWidth;
    let frameId = null;
    const observer = new ResizeObserver(() => {
      if (textarea.offsetWidth === lastWidth) return;
      lastWidth = textarea.offsetWidth;
      // resizing straight from the callback retriggers the observer in the same delivery
      // cycle, which the browser reports as an undelivered-notifications loop error
      frameId = requestAnimationFrame(adjustHeight);
    });
    observer.observe(textarea);
    return () => {
      observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [ref, adjustHeight]);

  return { ref, adjustHeight };
};

export default useAutoResizeTextarea;
